import { useMemo } from "react";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { itemLibraryApi, ItemLibraryItem, ItemLibraryResponse } from "../api/itemLibrary";
import { billOfQuantitiesApi } from "../api/billOfQuantities";
import { useUserRole } from "./useUserRole";
import { useBoqVersionHistory } from "./useBillOfQuantities";
import { useSiteOppNo } from "../features/rate-engine/hooks";
import { useTenderSiteScope } from "../contexts/TenderContext";

/**
 * Query keys for item library queries
 * Centralized to ensure consistency across the app
 */
export const ITEM_LIBRARY_QUERY_KEYS = {
  all: ["itemLibrary"] as const,
  byRegion: (region: string, siteName?: string, oppNo?: string) =>
    [...ITEM_LIBRARY_QUERY_KEYS.all, "region", region, siteName ?? "", oppNo ?? ""] as const,
  historicalSnapshot: (projectName: string, version: number) =>
    [...ITEM_LIBRARY_QUERY_KEYS.all, "historical-snapshot", projectName, version] as const,
};

type GroundworksItemLibraryMap = Record<
  string,
  Record<string, { resourceCode?: string; costCode?: string; nettRate?: number }>
>;

export function buildItemLibraryFromBoqSnapshot(boqData: unknown): ItemLibraryItem[] | null {
  const lib = (boqData as Record<string, unknown>)?.boq as unknown;
  const libraryMap = (lib as Record<string, unknown>)?.groundworksItemLibrary as GroundworksItemLibraryMap | undefined;
  if (!libraryMap || typeof libraryMap !== "object") return null;
  return buildItemsFromLibraryMap(libraryMap);
}

function buildItemsFromLibraryMap(lib: GroundworksItemLibraryMap): ItemLibraryItem[] | null {
  const seen = new Set<string>();
  const items: ItemLibraryItem[] = [];

  for (const tableEntries of Object.values(lib)) {
    if (!tableEntries || typeof tableEntries !== "object") continue;
    for (const entry of Object.values(tableEntries)) {
      if (!entry?.resourceCode) continue;
      const key = entry.resourceCode.trim().toUpperCase();
      if (seen.has(key)) continue;
      seen.add(key);
      items.push({
        region: "",
        libraryType: "",
        resourceCode: entry.resourceCode,
        resourceDescription: "",
        unit: "",
        baseRate: entry.nettRate ?? 0,
        nettRate: entry.nettRate ?? 0,
        resourceGroup: "",
        resourceType: "",
        costCodeCode: entry.costCode ?? "",
        costCodeDescription: "",
      });
    }
  }

  return items.length > 0 ? items : null;
}

/**
 * Hook to fetch item library by region.
 * Returns a flat array of ItemLibraryItem.
 *
 * When viewing a historical version (isHistoricalVersion === "true" in localStorage),
 * rates are sourced directly from the BOQ versions API (`groundworksItemLibrary` field
 * in the saved BOQ snapshot) so displayed rates always match what was priced at save time.
 * The query key is version-specific so switching between versions never returns stale cache.
 *
 * @param region - The region code (e.g., "north-west", "southern", etc.)
 * @param siteName - Optional site address for site-specific rates
 * @param options - React Query options
 */
export const useItemLibrary = (
  region: string,
  siteName?: string,
  options?: Omit<
    UseQueryOptions<ItemLibraryResponse, Error>,
    "queryKey" | "queryFn"
  >,
  forceUseLive?: boolean,
  oppNo?: string,
) => {
  // Always use the user's profile region for the templates API so every fetch
  // is scoped to the correct region regardless of which tender is open.
  // While the profile is still loading, hold off entirely — avoids a spurious
  // initial fetch using the fallback (tender) region before the profile resolves.
  const { region: profileRegion, isLoading: isProfileLoading } = useUserRole();
  const effectiveRegion = (profileRegion && profileRegion !== "All Regions")
    ? profileRegion
    : region;

  // forceUseLive bypasses the historical snapshot path (e.g. when BOQ status is in-progress).
  const isHistorical = !forceUseLive && localStorage.getItem("isHistoricalVersion") === "true";

  // Resolve effective site name: explicit arg takes priority, then fall back to the
  // current tender's projectName from localStorage so all callers automatically use
  // site-specific rates without needing to pass it explicitly.
  // The "~~" in project.region is used only as a flag to detect a site-specific tender;
  // the actual siteName passed to the API is the project name (not the site address).
  const tenderScope = useTenderSiteScope();
  // Inside an open tender the templates call is always scoped to that tender's site
  // (siteName + oppNo) — as long as the tender's region is the one being fetched, so a
  // tender from another region can't inject its site into this call.
  const tenderRegion = tenderScope.region?.split("~~").pop()?.trim().toLowerCase();
  const tenderMatchesRegion = !tenderRegion || tenderRegion === effectiveRegion.toLowerCase();
  const effectiveSiteName = useMemo(() => {
    if (siteName) return siteName;
    if (tenderScope.siteName && tenderMatchesRegion) return tenderScope.siteName;
    try {
      const raw = localStorage.getItem("currentTender");
      if (!raw) return undefined;
      const parsed = JSON.parse(raw);
      const regionValue = parsed?.data?.project?.region as string | undefined;
      if (!regionValue?.includes("~~")) return undefined;
      // Verify the stored tender's region matches the profile region before accepting
      // its site name — prevents a stale entry from a different region's site-specific
      // tender from injecting a wrong siteName into the API call.
      const storedRegion = regionValue.split("~~").pop()?.trim() ?? "";
      if (!storedRegion || storedRegion.toLowerCase() !== effectiveRegion.toLowerCase()) return undefined;
      const projectName = (parsed?.data?.project?.name as string | undefined)
        || (parsed?.data?.projectName as string | undefined);
      return projectName || undefined;
    } catch {
      return undefined;
    }
  }, [siteName, effectiveRegion, tenderScope.siteName, tenderMatchesRegion]);

  // Opportunity number disambiguating the site library (two sites can share a
  // name). Verified once — a site created before oppNo existed carries none, and
  // sending one for it would silently resolve to the regional library.
  const { oppNo: siteOppNo, isReady: isSiteOppNoReady } = useSiteOppNo(
    effectiveRegion,
    effectiveSiteName,
    effectiveSiteName ? (oppNo ?? tenderScope.oppNo) : undefined,
  );

  // Read the stored version info so we can build a version-specific cache key
  // and fetch the correct BOQ snapshot from the API.
  const boqVersionInfo = useMemo(() => {
    if (!isHistorical) return null;
    try {
      const raw = localStorage.getItem("currentBoqVersion");
      if (!raw) return null;
      return JSON.parse(raw) as { projectName?: string; version?: number };
    } catch {
      return null;
    }
  }, [isHistorical]);

  const hasVersionInfo =
    !!boqVersionInfo?.projectName && boqVersionInfo?.version != null;

  // Gate the snapshot path on actual stored version history — mirrors the check in
  // useBoqLatestVersion/useBoqSelectedVersion. When the API returns versions: [] (only
  // one version exists), hasActualVersionHistory is false and getBoqVersion is skipped.
  const { data: versionHistoryData, isLoading: isVersionHistoryLoading } = useBoqVersionHistory(
    isHistorical && hasVersionInfo ? (boqVersionInfo?.projectName ?? undefined) : undefined,
    { enabled: isHistorical && hasVersionInfo, oppNo },
  );
  const hasActualVersionHistory = (versionHistoryData?.versions?.length ?? 0) > 0;

  const queryKey = isHistorical && hasVersionInfo && hasActualVersionHistory
    ? ITEM_LIBRARY_QUERY_KEYS.historicalSnapshot(
        boqVersionInfo!.projectName!,
        boqVersionInfo!.version!,
      )
    : ITEM_LIBRARY_QUERY_KEYS.byRegion(effectiveRegion, effectiveSiteName, siteOppNo);

  // In view mode: wait for version history to resolve before deciding which path to take
  // so we don't fire the live API and then immediately replace it with the snapshot.
  // Once resolved: use snapshot when history exists, otherwise fall back to live.
  // In edit/review mode: always use live path.
  const computedEnabled = isHistorical && hasVersionInfo
    ? !isVersionHistoryLoading && (hasActualVersionHistory || (!!effectiveRegion && !isProfileLoading))
    : !!effectiveRegion && !isProfileLoading;
  const isEnabled = computedEnabled && isSiteOppNoReady;

  return useQuery<ItemLibraryResponse, Error>({
    queryKey,
    queryFn: async () => {
      if (isHistorical && hasVersionInfo && hasActualVersionHistory) {
        // Fetch the stored BOQ version from the API and extract groundworksItemLibrary.
        // This is always fresh from the server and is version-specific,
        // so cache cannot bleed between versions or be corrupted by auto-saves.
        const boqData = await billOfQuantitiesApi.getBoqVersion(
          boqVersionInfo!.projectName!,
          boqVersionInfo!.version!,
          oppNo,
        );

        const lib = boqData?.boq?.groundworksItemLibrary as
          | GroundworksItemLibraryMap
          | undefined;

        if (lib && typeof lib === "object") {
          const items = buildItemsFromLibraryMap(lib);
          if (items) return items;
        }

        // BOQ snapshot has no groundworksItemLibrary — fall back to live rate library
        return itemLibraryApi.fetchByRegion(effectiveRegion, effectiveSiteName, siteOppNo);
      }

      return itemLibraryApi.fetchByRegion(effectiveRegion, effectiveSiteName, siteOppNo);
    },
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    ...options,
    // Merge computed enabled with any caller-supplied enabled flag
    enabled: isEnabled && options?.enabled !== false,
  });
};

/**
 * Helper hook to get item library options for dropdowns.
 * Returns the items array directly.
 */
export const useItemLibraryOptions = (region: string) => {
  const { data, isLoading, error } = useItemLibrary(region);

  return {
    options: data ?? [],
    isLoading,
    error,
    count: data?.length ?? 0,
  };
};

/**
 * Helper hook to get a single item by resource code.
 */
export const useItemByCode = (region: string, code: string) => {
  const { data, isLoading, error } = useItemLibrary(region);

  const item: ItemLibraryItem | undefined = data?.find(
    (i) => i.resourceCode === code,
  );

  return { item, isLoading, error };
};
